import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Mail, Phone, Calendar as CalendarIcon, Clock, User, CreditCard, Trash2, StickyNote, Send, Pencil, X, Check, Loader2 } from "lucide-react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import RescheduleDialog from "@/components/dashboard/RescheduleDialog";
import ReservationActivity, { type ActivityItem } from "@/components/dashboard/ReservationActivity";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type ReservationStatus = "pending" | "confirmed" | "completed" | "no_show" | "cancelled" | "awaiting_payment" | "expired";

const statusColors: Record<string, string> = {
  confirmed: "bg-accent/10 text-accent border-accent/20",
  pending: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20",
  cancelled: "bg-destructive/10 text-destructive border-destructive/20",
  completed: "bg-primary/10 text-primary border-primary/20",
  no_show: "bg-destructive/10 text-destructive border-destructive/20",
  awaiting_payment: "bg-orange-500/10 text-orange-600 border-orange-500/20",
  expired: "bg-muted text-muted-foreground border-border",
};

interface ReservationData {
  id: string;
  reservation_number: string | null;
  date: string;
  duration: number;
  status: string;
  price: number;
  created_at: string;
  selected_options: string[] | null;
  organisation_id: string;
  customer: { id: string; name: string; email: string; phone: string | null } | null;
  service: { id: string; name: string; service_options: { id: string; name: string }[] } | null;
}

interface NoteData {
  id: string;
  text: string;
  created_at: string;
}

const ReservationDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { t } = useLanguage();

  const statusLabelKeys: Record<ReservationStatus, string> = {
    pending: "reservations.status_pending",
    confirmed: "reservations.status_confirmed",
    completed: "reservations.status_completed",
    no_show: "reservations.status_no_show",
    cancelled: "reservations.status_cancelled",
    awaiting_payment: "reservations.status_awaiting_payment",
    expired: "reservations.status_expired",
  };

  const [reservation, setReservation] = useState<ReservationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<ReservationStatus>("pending");
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [statusChangeTarget, setStatusChangeTarget] = useState<ReservationStatus | null>(null);

  const [notes, setNotes] = useState<NoteData[]>([]);
  const [newNote, setNewNote] = useState("");
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteText, setEditingNoteText] = useState("");

  const [activities, setActivities] = useState<ActivityItem[]>([]);

  useEffect(() => {
    const fetchReservation = async () => {
      if (!id) return;
      const { data, error } = await supabase
        .from("reservations")
        .select("*, customer:customers(id, name, email, phone), service:services(id, name, service_options(id, name))")
        .eq("id", id)
        .maybeSingle();

      if (error || !data) {
        setReservation(null);
        setLoading(false);
        return;
      }

      setReservation(data as any);
      setStatus((data.status as ReservationStatus) || "pending");

      const items: ActivityItem[] = [
        {
          id: "created",
          type: "created",
          label: t("reservations.created_label"),
          description: `${t("reservations.booked_by")} ${(data as any).customer?.name || "Unknown"} ${t("reservations.for")} ${(data as any).service?.name || "Unknown"}`,
          timestamp: new Date(data.created_at),
          icon: "dot",
        },
      ];

      // Load persisted activities from database
      const { data: dbActivities } = await supabase
        .from("reservation_activities")
        .select("id, type, label, description, icon, created_at")
        .eq("reservation_id", id)
        .order("created_at", { ascending: false });

      if (dbActivities) {
        dbActivities.forEach((a: any) => {
          items.push({
            id: a.id,
            type: a.type as ActivityItem["type"],
            label: a.label,
            description: a.description || undefined,
            timestamp: new Date(a.created_at),
            icon: (a.icon || "dot") as ActivityItem["icon"],
          });
        });
      }

      setActivities(items.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime()));

      const { data: notesData } = await supabase
        .from("reservation_notes")
        .select("id, text, created_at")
        .eq("reservation_id", id)
        .order("created_at", { ascending: false });

      setNotes(notesData || []);
      setLoading(false);
    };

    fetchReservation();
  }, [id]);

  const confirmStatusChange = async () => {
    if (!statusChangeTarget || !reservation) return;
    const previousStatus = status;

    // If changing to cancelled, use the manage-booking edge function to send notifications
    if (statusChangeTarget === "cancelled" && reservation.customer?.email) {
      const { data: resData } = await supabase
        .from("reservations")
        .select("language")
        .eq("id", reservation.id)
        .single();

      const lang = (resData as any)?.language || "nl";

      const { data, error } = await supabase.functions.invoke("manage-booking", {
        body: {
          action: "cancel",
          reservation_id: reservation.id,
          email: reservation.customer.email,
          notify_customer: true,
          notify_organisation: false,
          language: lang,
          source: "dashboard",
        },
      });

      if (error || data?.error) {
        toast.error(data?.error || "Failed to cancel");
        setStatusChangeTarget(null);
        return;
      }
    } else {
      const { error } = await supabase
        .from("reservations")
        .update({ status: statusChangeTarget })
        .eq("id", reservation.id);

      if (error) {
        toast.error(error.message);
        setStatusChangeTarget(null);
        return;
      }
    }

    setStatus(statusChangeTarget);

    const iconMap: Record<ReservationStatus, ActivityItem["icon"]> = {
      confirmed: "check",
      completed: "check",
      pending: "clock",
      cancelled: "cancel",
      no_show: "warning",
      awaiting_payment: "clock",
      expired: "clock",
    };

    const activityIcon = iconMap[statusChangeTarget];
    const activityLabel = `${t("reservations.status_changed_to")} ${t(statusLabelKeys[statusChangeTarget])}`;
    const activityDesc = `${t("reservations.changed_from")} ${t(statusLabelKeys[previousStatus])}`;

    // For cancellations, the edge function already logs the activity — only persist for other status changes
    if (statusChangeTarget !== "cancelled") {
      const { data: inserted } = await supabase
        .from("reservation_activities")
        .insert({
          reservation_id: reservation.id,
          organisation_id: reservation.organisation_id,
          type: "status_change",
          label: activityLabel,
          description: activityDesc,
          icon: activityIcon,
        })
        .select("id, created_at")
        .single();

      setActivities((prevActivities) => [
        {
          id: inserted?.id || `status-${Date.now()}`,
          type: "status_change",
          label: activityLabel,
          description: activityDesc,
          timestamp: inserted ? new Date(inserted.created_at) : new Date(),
          icon: activityIcon,
        },
        ...prevActivities,
      ]);
    } else {
      // Refetch activities to pick up what the edge function logged
      const { data: dbActivities } = await supabase
        .from("reservation_activities")
        .select("id, type, label, description, icon, created_at")
        .eq("reservation_id", reservation.id)
        .order("created_at", { ascending: false });

      if (dbActivities) {
        const createdActivity: ActivityItem = {
          id: "created",
          type: "created",
          label: t("reservations.created_label"),
          description: `${t("reservations.booked_by")} ${reservation.customer?.name || "Unknown"} ${t("reservations.for")} ${reservation.service?.name || "Unknown"}`,
          timestamp: new Date(reservation.created_at),
          icon: "dot",
        };
        const mapped: ActivityItem[] = dbActivities.map((a: any) => ({
          id: a.id,
          type: a.type as ActivityItem["type"],
          label: a.label,
          description: a.description || undefined,
          timestamp: new Date(a.created_at),
          icon: (a.icon || "dot") as ActivityItem["icon"],
        }));
        setActivities([...mapped, createdActivity].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime()));
      }
    }

    toast.success(`${t("reservations.status_updated_to")} ${t(statusLabelKeys[statusChangeTarget])}`);
    setStatusChangeTarget(null);
  };

  const handleRescheduleSuccess = useCallback((newDate: Date, newTime: string) => {
    if (!reservation || !id) return;

    // Refetch reservation and activities (edge function already persisted the activity)
    supabase
      .from("reservations")
      .select("*, customer:customers(id, name, email, phone), service:services(id, name, service_options(id, name))")
      .eq("id", id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setReservation(data as any);
          setStatus((data.status as ReservationStatus) || "pending");
        }
      });

    // Refetch activities from DB
    supabase
      .from("reservation_activities")
      .select("id, type, label, description, icon, created_at")
      .eq("reservation_id", id)
      .order("created_at", { ascending: false })
      .then(({ data: dbActivities }) => {
        const createdActivity: ActivityItem = {
          id: "created",
          type: "created",
          label: t("reservations.created_label"),
          description: `${t("reservations.booked_by")} ${reservation.customer?.name || "Unknown"} ${t("reservations.for")} ${reservation.service?.name || "Unknown"}`,
          timestamp: new Date(reservation.created_at),
          icon: "dot",
        };
        const mapped: ActivityItem[] = (dbActivities || []).map((a: any) => ({
          id: a.id,
          type: a.type as ActivityItem["type"],
          label: a.label,
          description: a.description || undefined,
          timestamp: new Date(a.created_at),
          icon: (a.icon || "dot") as ActivityItem["icon"],
        }));
        setActivities([...mapped, createdActivity].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime()));
      });
  }, [reservation, id, t]);

  const handleAddNote = async () => {
    if (!newNote.trim() || !reservation) return;
    const { data, error } = await supabase
      .from("reservation_notes")
      .insert({ reservation_id: reservation.id, text: newNote.trim() })
      .select("id, text, created_at")
      .single();

    if (error) {
      toast.error(error.message);
      return;
    }

    setNotes((prev) => [data, ...prev]);
    setActivities((prev) => [
      {
        id: `act-${Date.now()}`,
        type: "note_added" as ActivityItem["type"],
        label: t("reservations.note_added_label"),
        description: newNote.trim().length > 80 ? newNote.trim().slice(0, 80) + "…" : newNote.trim(),
        timestamp: new Date(),
        icon: "dot",
      },
      ...prev,
    ]);
    setNewNote("");
    toast.success(t("reservations.note_added"));
  };

  const handleUpdateNote = async (noteId: string) => {
    if (!editingNoteText.trim()) return;
    const { error } = await supabase
      .from("reservation_notes")
      .update({ text: editingNoteText.trim() })
      .eq("id", noteId);

    if (error) {
      toast.error(error.message);
      return;
    }

    setNotes((prev) =>
      prev.map((n) => (n.id === noteId ? { ...n, text: editingNoteText.trim() } : n))
    );
    setEditingNoteId(null);
    toast.success(t("reservations.note_updated"));
  };

  const handleDeleteNote = async (noteId: string) => {
    const { error } = await supabase
      .from("reservation_notes")
      .delete()
      .eq("id", noteId);

    if (error) {
      toast.error(error.message);
      return;
    }

    setNotes((prev) => prev.filter((n) => n.id !== noteId));
    toast.success(t("reservations.note_deleted"));
  };

  const handleDeleteReservation = async () => {
    if (!reservation) return;
    const { error } = await supabase
      .from("reservations")
      .delete()
      .eq("id", reservation.id);

    if (error) {
      toast.error(error.message);
      return;
    }

    toast.success(t("reservations.deleted"));
    navigate("/dashboard/reservations");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!reservation) {
    return (
      <div className="p-6">
        <Button variant="ghost" size="sm" asChild className="mb-4 -ml-2 text-muted-foreground">
          <Link to="/dashboard/reservations">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("reservations.back")}
          </Link>
        </Button>
        <p className="text-muted-foreground">{t("reservations.not_found")}</p>
      </div>
    );
  }

  const dateObj = new Date(reservation.date);
  const endTime = new Date(dateObj.getTime() + reservation.duration * 60000);

  return (
    <div className="p-6">
      <Button variant="ghost" size="sm" asChild className="mb-4 -ml-2 text-muted-foreground">
        <Link to="/dashboard/reservations">
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t("reservations.back")}
        </Link>
      </Button>

      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-xl font-bold text-foreground">{reservation.customer?.name || t("reservations.title")}</h1>
            <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${statusColors[status]}`}>
              {t(statusLabelKeys[status])}
            </span>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mt-1">
            {reservation.reservation_number && (
              <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded select-all w-fit">{reservation.reservation_number}</span>
            )}
            <span className="text-xs sm:text-sm text-muted-foreground break-words">
              {t("reservations.booked_on")} {format(new Date(reservation.created_at), "MMM d, yyyy")} {t("reservations.at")} {format(new Date(reservation.created_at), "HH:mm")}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={status} onValueChange={(v) => setStatusChangeTarget(v as ReservationStatus)}>
            <SelectTrigger className="w-auto min-w-[120px] h-9 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">{t("reservations.status_pending")}</SelectItem>
              <SelectItem value="confirmed">{t("reservations.status_confirmed")}</SelectItem>
              <SelectItem value="completed">{t("reservations.status_completed")}</SelectItem>
              <SelectItem value="no_show">{t("reservations.status_no_show")}</SelectItem>
              <SelectItem value="cancelled">{t("reservations.status_cancelled")}</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" onClick={() => setRescheduleOpen(true)}>{t("reservations.reschedule")}</Button>
          <Button
            variant="outline"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={() => setDeleteOpen(true)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Customer Info */}
        <section className="rounded-xl border border-border bg-card p-6 shadow-card">
          <h2 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
            <User className="h-4 w-4 text-muted-foreground" />
            {t("reservations.customer")}
          </h2>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                {(reservation.customer?.name || "?").split(" ").map((n) => n[0]).join("")}
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">{reservation.customer?.name || "—"}</p>
                {reservation.customer?.id && (
                  <Link to={`/dashboard/customers/${reservation.customer.id}`} className="text-xs text-primary hover:underline">
                    {t("reservations.view_profile")}
                  </Link>
                )}
              </div>
            </div>
            <div className="space-y-2 pt-2 border-t border-border">
              <div className="flex items-center gap-2 text-sm">
                <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-foreground">{reservation.customer?.email || "—"}</span>
              </div>
              {reservation.customer?.phone && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-foreground">{reservation.customer.phone}</span>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Appointment Details */}
        <section className="rounded-xl border border-border bg-card p-6 shadow-card">
          <h2 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
            <CalendarIcon className="h-4 w-4 text-muted-foreground" />
            {t("reservations.appointment")}
          </h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{t("reservations.date")}</span>
              <span className="font-medium text-foreground">{format(dateObj, "EEEE, MMMM d, yyyy")}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{t("reservations.time")}</span>
              <span className="font-medium text-foreground">
                {format(dateObj, "HH:mm")} – {format(endTime, "HH:mm")}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{t("reservations.duration")}</span>
              <span className="font-medium text-foreground">{reservation.duration} min</span>
            </div>
          </div>
        </section>

        {/* Service Details */}
        <section className="rounded-xl border border-border bg-card p-6 shadow-card">
          <h2 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            {t("reservations.service")}
          </h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{t("reservations.service")}</span>
              <span className="font-semibold text-foreground">{reservation.service?.name || "—"}</span>
            </div>
            {(() => {
              const allOptions = reservation.service?.service_options || [];
              const selectedIds = reservation.selected_options || [];
              const selectedOptions = allOptions.filter(o => selectedIds.includes(o.id));
              if (selectedOptions.length === 0) return null;
              return (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{t("reservations.options")}</span>
                  <div className="flex flex-wrap gap-1 justify-end">
                    {selectedOptions.map(o => (
                      <Badge key={o.id} variant="outline" className="text-xs">{o.name}</Badge>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        </section>

        {/* Payment */}
        <section className="rounded-xl border border-border bg-card p-6 shadow-card">
          <h2 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-muted-foreground" />
            {t("reservations.payment")}
          </h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t("reservations.total_amount")}</span>
              <div className="rounded-lg bg-primary/10 px-3 py-1.5">
                <span className="text-xl font-bold text-primary">€{Number(reservation.price).toFixed(2)}</span>
              </div>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{t("reservations.payment_status")}</span>
              {(() => {
                const price = Number(reservation.price);
                if (price === 0) {
                  return (
                    <Badge variant="outline" className="text-xs bg-accent/10 text-accent border-accent/20">
                      {t("reservations.free")}
                    </Badge>
                  );
                }
                if (reservation.status === "awaiting_payment") {
                  return (
                    <Badge variant="outline" className="text-xs bg-orange-500/10 text-orange-600 border-orange-500/20">
                      {t("reservations.status_awaiting_payment")}
                    </Badge>
                  );
                }
                const hasPaidActivity = activities.some(
                  (a) => a.type === "payment" && a.label === "Payment received"
                );
                if (hasPaidActivity) {
                  return (
                    <Badge variant="outline" className="text-xs bg-accent/10 text-accent border-accent/20">
                      {t("reservations.paid_online")}
                    </Badge>
                  );
                }
                const hasExpiredPayment = activities.some(
                  (a) => a.label === "Payment expired"
                );
                if (hasExpiredPayment) {
                  return (
                    <Badge variant="outline" className="text-xs bg-muted text-muted-foreground border-border">
                      {t("reservations.status_expired")}
                    </Badge>
                  );
                }
                return (
                  <Badge variant="outline" className="text-xs bg-muted text-muted-foreground border-border">
                    {t("reservations.pay_at_location")}
                  </Badge>
                );
              })()}
            </div>
          </div>
        </section>
      </div>

      {/* Notes & Activity side by side */}
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        {/* Internal Notes */}
        <section className="rounded-xl border border-border bg-card p-6 shadow-card">
          <h2 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
            <StickyNote className="h-4 w-4 text-muted-foreground" />
            {t("reservations.notes")}
          </h2>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Textarea
                placeholder={t("reservations.notes_placeholder")}
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                rows={2}
                className="resize-none"
              />
              <Button
                size="icon"
                className="shrink-0 self-end h-9 w-9"
                disabled={!newNote.trim()}
                onClick={handleAddNote}
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
            {notes.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">{t("reservations.no_notes")}</p>
            ) : (
              <div className="space-y-2 mt-2">
                {notes.map((note) => (
                  <div key={note.id} className="rounded-lg border border-border bg-muted/30 p-3 group">
                    {editingNoteId === note.id ? (
                      <div className="space-y-2">
                        <Textarea
                          value={editingNoteText}
                          onChange={(e) => setEditingNoteText(e.target.value)}
                          rows={2}
                          className="resize-none"
                          autoFocus
                        />
                        <div className="flex gap-1 justify-end">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => setEditingNoteId(null)}
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="icon"
                            className="h-7 w-7"
                            disabled={!editingNoteText.trim()}
                            onClick={() => handleUpdateNote(note.id)}
                          >
                            <Check className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm text-foreground whitespace-pre-wrap flex-1">{note.text}</p>
                          <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-muted-foreground hover:text-foreground"
                              onClick={() => {
                                setEditingNoteId(note.id);
                                setEditingNoteText(note.text);
                              }}
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-muted-foreground hover:text-destructive"
                              onClick={() => handleDeleteNote(note.id)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-1.5">
                          {format(new Date(note.created_at), "MMM d, yyyy 'at' HH:mm")}
                        </p>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Activity History */}
        <ReservationActivity activities={activities} />
      </div>

      <RescheduleDialog
        open={rescheduleOpen}
        onOpenChange={setRescheduleOpen}
        currentDate={dateObj}
        duration={reservation.duration}
        reservationId={reservation.id}
        organisationId={reservation.organisation_id}
        onSuccess={handleRescheduleSuccess}
      />

      {/* Status change confirmation */}
      <AlertDialog open={!!statusChangeTarget} onOpenChange={(open) => !open && setStatusChangeTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("reservations.status_change_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("reservations.status_change_desc_pre")} <span className="font-medium text-foreground">{t(statusLabelKeys[status])}</span> {t("reservations.status_change_desc_to")} <span className="font-medium text-foreground">{statusChangeTarget ? t(statusLabelKeys[statusChangeTarget]) : ""}</span>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("reservations.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmStatusChange}>
              {t("reservations.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("reservations.delete_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("reservations.delete_desc")} <span className="font-medium text-foreground">{reservation.customer?.name || t("reservations.this_customer")}</span>{t("reservations.delete_undone")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("reservations.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteReservation}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("reservations.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ReservationDetailPage;